/**
 * Money helpers for the Admin Pricing Manager.
 *
 * Rules:
 *   - Admin types dollars naturally: "0.40", "1", "1.50", "3", "20".
 *   - We convert to INTEGER cents at the boundary before calling the
 *     backend. Never send floats — cents are the canonical unit.
 *   - Prices are non-negative.
 *   - At most 2 decimal places allowed ("1.005" is rejected).
 *
 * Exposed pure functions so we can unit-test them in Jest.
 */

/** Return `{ cents, error }`. `cents` is null when input is invalid. */
export function dollarsInputToCents(raw) {
  if (raw == null) return { cents: null, error: "Enter a price" };
  const s = String(raw).trim();
  if (!s) return { cents: null, error: "Enter a price" };
  // Reject obviously wrong shapes (letters, negative, currency symbols).
  if (!/^\d+(\.\d{1,2})?$/.test(s)) {
    return {
      cents: null,
      error:
        "Enter a whole-dollar or two-decimal amount (e.g. 3 or 1.50). Negative values are not allowed.",
    };
  }
  const parts = s.split(".");
  const whole = parseInt(parts[0], 10);
  const frac = parts[1] ? parts[1].padEnd(2, "0") : "00";
  const fracInt = parseInt(frac, 10);
  const cents = whole * 100 + fracInt;
  if (!Number.isFinite(cents) || cents < 0) {
    return { cents: null, error: "Price must be non-negative" };
  }
  return { cents, error: null };
}

/** Format integer cents as "$X.XX" (or "—" for null). */
export function centsToDisplayDollars(cents) {
  if (cents == null) return "—";
  const n = Number(cents) / 100;
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}
